/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2025 HG Digital/Setera
 * Todos os direitos reservados.
 *
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 08-01-2025
 * Autor     : System
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { Observable } from "rxjs";
import {
  STORAGE_KEY_ACCESS_TOKEN_KEYCLOAK,
  STORAGE_KEY_ACCESS_TOKEN_WSO2,
} from "src/app/storageCore/constStorageKeys";
import storageX from "src/app/storageCore/storageX";

/**
 * Interface para representar um dashboard do Superset
 */
export interface SupersetDashboard {
  id: string;
  dashboard_title: string;
  slug?: string;
  status?: string;
  /** ID de embed (embed_id) usado pelo Superset Embedded SDK */
  embed_id?: string;
}

/**
 * Serviço responsável por gerenciar a comunicação com o backend
 * para obter tokens de acesso ao Superset e listar dashboards
 */
@Injectable({
  providedIn: "root",
})
export class SupersetService {
  private readonly apiBaseUrl = environment.apiSuperset;

  constructor(private http: HttpClient) {}

  /** Base URL da API (para montar frame URL do iframe no mesmo origem). */
  getApiBaseUrl(): string {
    return this.apiBaseUrl;
  }

  /**
   * Monta os headers de autenticação para o gateway (WSO2 e Keycloak).
   */
  private getAuthHeaders(): HttpHeaders {
    const token = storageX.whereKeyIs(STORAGE_KEY_ACCESS_TOKEN_WSO2).get();

    let headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    if (token) {
      headers = headers.set("Authorization", `Bearer ${token}`);
    }

    const keycloakToken = storageX
      .whereKeyIs(STORAGE_KEY_ACCESS_TOKEN_KEYCLOAK)
      .get();
    if (keycloakToken) {
      headers = headers.set("kc-auth", "Bearer " + keycloakToken);
    }

    return headers;
  }

  /**
   * Lista dashboards e retorna a base do Superset.
   * O front usa esses dados para listar dashboards e saber o domínio do Superset.
   * O guest token é obtido por dashboard em getDashboardToken().
   */
  public listDashboards(): Observable<{
    dashboards: SupersetDashboard[];
    supersetBaseUrl: string;
  }> {
    const url = `${this.apiBaseUrl}/superset/dashboard`;
    return this.http.get<{
      dashboards: SupersetDashboard[];
      supersetBaseUrl: string;
    }>(url, { headers: this.getAuthHeaders() });
  }

  /**
   * Obtém guest token para um dashboard específico.
   * Usado pelo fetchGuestToken do @superset-ui/embedded-sdk.
   */
  public getDashboardToken(
    dashboardId: string,
  ): Observable<{ guestToken: string; supersetBaseUrl: string }> {
    const url = `${this.apiBaseUrl}/superset/dashboard/${dashboardId}/token`;
    return this.http.get<{ guestToken: string; supersetBaseUrl: string }>(url, {
      headers: this.getAuthHeaders(),
    });
  }
}
